/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import { useCallback, useEffect, useRef, useState } from "react";
import Moment from "react-moment";
import { Table } from "react-bootstrap";
import { ENDPOINT } from "../../services/api-endpoint";
import { getList, getLists } from "../../services/services";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import {
  extractPartnerVendorOrdersPayload,
  normalizeManualOrder,
  normalizePlatformVendorOrder,
  sortOrdersByCreatedDesc,
  type OrderSourceTab,
  type UiOrder,
} from "./mes-order-utils";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import "./mes-orders.scss";

const MesOrders = () => {
  const [activeTab, setActiveTab] = useState<OrderSourceTab>("all");
  const [clientList, setClientList] = useState<any[]>([]);
  const [mergedAllRows, setMergedAllRows] = useState<UiOrder[]>([]);
  const [manualPageRows, setManualPageRows] = useState<UiOrder[]>([]);
  const [manualTotal, setManualTotal] = useState(0);
  const [platformPageRows, setPlatformPageRows] = useState<UiOrder[]>([]);
  const [platformTotal, setPlatformTotal] = useState(0);
  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchText, setSearchText] = useState("");
  /** Search applied after Enter / search icon (platform tab uses this for Partner API) */
  const [appliedSearch, setAppliedSearch] = useState("");
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  const navigate = useNavigate();
  const { isLoading, runWithLoading } = usePageLoader();

  /** Keep stable so order list fetch effects do not re-run when clients load */
  const clientListRef = useRef(clientList);
  clientListRef.current = clientList;

  const resolveClientName = useCallback((id: string | number | null | undefined) => {
    const c = clientListRef.current.find((x) => String(x.id) === String(id));
    return c?.companyorgname || c?.clientname || (id != null ? `Client #${id}` : "—");
  }, []);

  useEffect(() => {
    (async () => {
      const data = await getList(ENDPOINT.CLIENT_LIST);
      setClientList(Array.isArray(data) ? data : []);
    })();
  }, []);

  const loadManualPage = useCallback(
    async (nextPage: number, overrideSearch?: string) => {
      await runWithLoading(async () => {
        const q = overrideSearch !== undefined ? overrideSearch : searchTextRef.current;
        const list = await getLists(ENDPOINT.ORDER_LIST, {
          pagesize: pageSize,
          pagenumber: nextPage,
          serachtext: q,
        });
        const rows = (list?.data as unknown[]) || [];
        const count = Number(list?.count ?? 0);
        setManualTotal(count);
        setManualPageRows(
          rows.map((r: any) => normalizeManualOrder(r, resolveClientName)),
        );
        setPageIndex(nextPage);
      });
    },
    [pageSize, runWithLoading, resolveClientName],
  );

  const loadMergedAll = useCallback(async () => {
    await runWithLoading(async () => {
      const q = searchTextRef.current;
      const [list, platRaw] = await Promise.all([
        getLists(ENDPOINT.ORDER_LIST, {
          pagesize: 500,
          pagenumber: 0,
          serachtext: q,
        }),
        getList(ENDPOINT.PARTNER_VENDOR_ACTIVE_POS, {
          page: 1,
          search: q,
        }).catch(() => null),
      ]);

      const rows = (list?.data as unknown[]) || [];
      const count = Number(list?.count ?? 0);
      setManualTotal(count);

      const manualUi = rows.map((r: any) =>
        normalizeManualOrder(r, resolveClientName),
      );

      let platformUi: UiOrder[] = [];
      let platTotal = 0;
      if (platRaw != null) {
        const extracted = extractPartnerVendorOrdersPayload(platRaw);
        platTotal = extracted.total;
        platformUi = extracted.rows.map((r) => normalizePlatformVendorOrder(r));
      }
      setPlatformTotal(platTotal);

      setMergedAllRows(sortOrdersByCreatedDesc([...platformUi, ...manualUi]));
      setPageIndex(0);
    });
  }, [resolveClientName, runWithLoading]);

  const loadPlatformPage = useCallback(
    async (nextPage: number, overrideSearch?: string) => {
      await runWithLoading(async () => {
        const q = overrideSearch !== undefined ? overrideSearch : appliedSearch;
        const raw = await getList(ENDPOINT.PARTNER_VENDOR_ACTIVE_POS, {
          page: nextPage + 1,
          search: q,
        });
        const { rows, total } = extractPartnerVendorOrdersPayload(raw);
        setPlatformPageRows(rows.map((r) => normalizePlatformVendorOrder(r)));
        setPlatformTotal(total);
        setPageIndex(nextPage);
      });
    },
    [appliedSearch, runWithLoading],
  );

  /** Badge count for Truventor Platform tab (best-effort; requires Partner proxy auth). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await getList(ENDPOINT.PARTNER_VENDOR_ACTIVE_POS, {
          page: 1,
          search: "",
        });
        const { total } = extractPartnerVendorOrdersPayload(raw);
        if (!cancelled) setPlatformTotal(total);
      } catch {
        /* Partner API optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Do not wait for client list — orders must load even when CLIENT_LIST is empty
    // or fails; client names use Client #id until clients load.
    if (activeTab === "all") {
      loadMergedAll();
    } else if (activeTab === "manual") {
      loadManualPage(0);
    } else if (activeTab === "platform") {
      loadPlatformPage(0);
    } else {
      setPageIndex(0);
    }
  }, [activeTab, loadMergedAll, loadManualPage, loadPlatformPage]);

  /** Refresh client labels when clients arrive — no extra orders API call */
  useEffect(() => {
    const label = (id: string | number | null | undefined) => {
      const c = clientList.find((x) => String(x.id) === String(id));
      return c?.companyorgname || c?.clientname || (id != null ? `Client #${id}` : "—");
    };
    setManualPageRows((prev) =>
      prev.map((u) =>
        u.source === "manual" ? { ...u, clientName: label(u.clientid) } : u,
      ),
    );
    setMergedAllRows((prev) =>
      prev.map((u) =>
        u.source === "manual" ? { ...u, clientName: label(u.clientid) } : u,
      ),
    );
  }, [clientList]);

  const handleSearch = () => {
    searchTextRef.current = searchText;
    setAppliedSearch(searchText);
    setPageIndex(0);
    if (activeTab === "all") {
      loadMergedAll();
    } else if (activeTab === "manual") {
      loadManualPage(0, searchText);
    } else if (activeTab === "platform") {
      loadPlatformPage(0, searchText);
    }
  };

  const goDetail = (o: UiOrder) => {
    navigate(`/mes-order/order/${o.id}`, { state: { order: o } });
  };

  const goCad = (e: React.MouseEvent, o: UiOrder) => {
    e.stopPropagation();
    navigate("/mes-order/cad-manufacturing", { state: { order: o } });
  };

  const statusPillClass = (s: string) => {
    if (s === "In Progress") return "tv-pill tv-pill--status-progress";
    if (s === "Open") return "tv-pill tv-pill--status-open";
    return "tv-pill tv-pill--status-draft";
  };

  const allSliceBase = mergedAllRows;
  const allTotalForPaging = allSliceBase.length;
  const allPageRows = allSliceBase.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize,
  );

  const manualTotalPages = Math.max(1, Math.ceil(manualTotal / pageSize));
  const allTotalPages = Math.max(1, Math.ceil(allTotalForPaging / pageSize));
  const platformTotalPages = Math.max(1, Math.ceil(platformTotal / pageSize));

  let displayRows: UiOrder[] = [];
  if (activeTab === "all") displayRows = allPageRows;
  else if (activeTab === "manual") displayRows = manualPageRows;
  else displayRows = platformPageRows;

  const totalPages =
    activeTab === "all"
      ? allTotalPages
      : activeTab === "manual"
        ? manualTotalPages
        : platformTotalPages;

  const hasRecords = displayRows.length > 0;
  const showSearch = true;
  const showEmpty = !hasRecords;
  const showPagination = totalPages > 1;

  const allOrdersCount = manualTotal + platformTotal;
  const tabCounts = {
    all: allOrdersCount,
    platform: platformTotal,
    manual: manualTotal,
  } as const;

  const onPageChange = (i: number) => {
    if (activeTab === "manual") {
      loadManualPage(i);
    } else if (activeTab === "platform") {
      loadPlatformPage(i);
    } else {
      setPageIndex(i);
    }
  };

  const renderRow = (list: UiOrder) => {
    const isManual = list.source === "manual";
    return (
      <tr
        key={`${list.id}-${list.ordercode}`}
        className="tv-order-tr"
        onClick={() => goDetail(list)}
      >
        <td>
          <span
            className="tv-order-code-link"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              goDetail(list);
            }}
            onKeyDown={(e) => e.key === "Enter" && goDetail(list)}
          >
            {list.ordercode}
          </span>
        </td>
        <td>
          {isManual ? (
            <span className="tv-pill tv-pill--source-manual">
              <i className="fa fa-upload" aria-hidden="true" />
              &nbsp;Manual
            </span>
          ) : (
            <span className="tv-pill tv-pill--source-platform">
              <i className="fa fa-cube" aria-hidden="true" />
              &nbsp;Platform
            </span>
          )}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          {isManual ? (
            <span
              className="tv-cad-cell-link"
              role="button"
              tabIndex={0}
              onClick={(e) => goCad(e, list)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  goCad(e as unknown as React.MouseEvent, list);
                }
              }}
            >
              <i className="fa fa-upload" aria-hidden="true" />
              Upload CAD
            </span>
          ) : (
            <span className="tv-cad-auto">
              <i className="fa fa-check-circle" aria-hidden="true" />
              Auto-processed
            </span>
          )}
        </td>
        <td>
          <Moment format="D MMM YYYY">{list.createdate}</Moment>
        </td>
        <td>
          {list.duedate ? <Moment format="D MMM YYYY">{list.duedate}</Moment> : "—"}
        </td>
        <td>{list.clientName}</td>
        <td>{list.partsCount}</td>
        <td>
          <span className={statusPillClass(list.status)}>{list.status}</span>
        </td>
      </tr>
    );
  };

  return (
    <div>
      {isLoading && <AppLoader text="Loading orders..." />}
      <div className="parts-details tv-page-content tv-production-orders-page">
          <div className="tv-list-head-row">
            <button
              type="button"
              className="btn btn-primary tv-list-add-btn"
              onClick={() => navigate(`/create-order`)}
            >
              <span>
                <i className="fa fa-plus-circle" aria-hidden="true"></i>
              </span>
              Create Manual Order
            </button>
          </div>

          <div className="tv-order-summary-cards" role="tablist" aria-label="Order sources">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "all"}
              className={`tv-order-summary-card ${
                activeTab === "all" ? "tv-order-summary-card--active" : ""
              }`}
              onClick={() => {
                setActiveTab("all");
                setPageIndex(0);
              }}
            >
              <div className="tv-order-summary-card-top">
                <span className="tv-order-summary-card-label">All Orders</span>
                <i
                  className={`fa fa-shopping-cart tv-order-summary-card-icon ${
                    activeTab === "all" ? "tv-order-summary-card-icon--accent" : ""
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className="tv-order-summary-card-count">{tabCounts.all}</div>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "platform"}
              className={`tv-order-summary-card ${
                activeTab === "platform" ? "tv-order-summary-card--active" : ""
              }`}
              onClick={() => {
                setActiveTab("platform");
                setPageIndex(0);
              }}
            >
              <div className="tv-order-summary-card-top">
                <span className="tv-order-summary-card-label">Truventor Platform</span>
                <i
                  className={`fa fa-cube tv-order-summary-card-icon ${
                    activeTab === "platform" ? "tv-order-summary-card-icon--accent" : ""
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className="tv-order-summary-card-count">{tabCounts.platform}</div>
              <p className="tv-order-summary-card-hint">
                CAD files auto-processed via order pipeline
              </p>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "manual"}
              className={`tv-order-summary-card ${
                activeTab === "manual" ? "tv-order-summary-card--active" : ""
              }`}
              onClick={() => {
                setActiveTab("manual");
                setPageIndex(0);
              }}
            >
              <div className="tv-order-summary-card-top">
                <span className="tv-order-summary-card-label">Manual Orders</span>
                <i
                  className={`fa fa-upload tv-order-summary-card-icon ${
                    activeTab === "manual" ? "tv-order-summary-card-icon--accent" : ""
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className="tv-order-summary-card-count">{tabCounts.manual}</div>
              <p className="tv-order-summary-card-hint">
                CAD files require manual upload for CAM conversion
              </p>
            </button>
          </div>

          <div className="clearfix pt-20">
            <div className="tv-list-panel">
              {showSearch && (
                <div className="tv-order-search-wrap">
                  <div className="tv-list-search-row">
                    <input
                      type="text"
                      placeholder="Search Orders"
                      className="form-control search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          searchTextRef.current = (e.target as HTMLInputElement).value;
                          handleSearch();
                        }
                      }}
                    />
                    <i
                      className="fa fa-search"
                      aria-hidden="true"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSearch()}
                    />
                  </div>
                </div>
              )}

              <div className="tv-order-table-wrap">
                {showEmpty ? (
                  <MesEmptyState
                    icon="fa-shopping-cart"
                    title="No Orders Found"
                    subtitle="Try another search or create a manual order."
                  />
                ) : (
                  <Table responsive className="tv-order-table mb-0">
                    <thead>
                      <tr>
                        <th className="tv-order-th">Order code</th>
                        <th className="tv-order-th">Source</th>
                        <th className="tv-order-th">CAD / CAM</th>
                        <th className="tv-order-th">Created on</th>
                        <th className="tv-order-th">Due date</th>
                        <th className="tv-order-th">Client</th>
                        <th className="tv-order-th">Parts</th>
                        <th className="tv-order-th">Status</th>
                      </tr>
                    </thead>
                    <tbody>{displayRows.map((row) => renderRow(row))}</tbody>
                  </Table>
                )}
              </div>

              {showPagination && !showEmpty && (
                <div className="paginate mt-10 tv-list-pagination">
                  <nav aria-label="Order pagination">
                    <ul className="pagination">
                      <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                        <a
                          className="page-link"
                          href="#"
                          aria-label="Previous"
                          onClick={(e) => {
                            e.preventDefault();
                            if (pageIndex > 0) onPageChange(pageIndex - 1);
                          }}
                        >
                          <span aria-hidden="true">&laquo;</span>
                        </a>
                      </li>
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <li
                          key={`${activeTab}-p-${i}`}
                          className={`page-item ${i === pageIndex ? "active" : ""}`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onPageChange(i);
                            }}
                          >
                            {i + 1}
                          </a>
                        </li>
                      ))}
                      <li
                        className={`page-item ${
                          pageIndex >= totalPages - 1 ? "disabled" : ""
                        }`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          aria-label="Next"
                          onClick={(e) => {
                            e.preventDefault();
                            if (pageIndex < totalPages - 1) onPageChange(pageIndex + 1);
                          }}
                        >
                          <span aria-hidden="true">&raquo;</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};
export default MesOrders;
